import { SelectProps } from 'antd';
import { useEffect, useState } from 'react';
import { useDebounce } from '@/hooks';
import InfiniteSelect from '@/components/_common/InfiniteSelect';
import { BankAccountsQuery } from '@/core/domain/enterprise/BankAccounts/BankAccounts.types';
import {
  useBanks,
  useBankMutation,
} from '@/core/domain/enterprise/Banks/Banks.hooks';
import useSelectOptions from '@/hooks/useSelectOptions';
import { Bank } from '@/core/domain/enterprise/Banks/Banks.types';
import { flatInfiniteData } from '@/utils/request/flatInfiniteData';

type Props = Omit<SelectProps, 'options'> & {
  queryFilter?: BankAccountsQuery;
  queryEnable?: boolean;
  onBankSelected?: (bank: Bank) => void;
};

const BanksInfiniteSelectField: React.FC<Props> = ({
  queryFilter,
  queryEnable = false,
  ...props
}) => {
  const [selectClicked, setSelectClicked] = useState<boolean>(false);
  const [searchValue, setSearchValue] = useState<string>();
  const search = useDebounce(searchValue);

  const banksList = useBanks(
    {
      ...queryFilter,
      rows: 10,
      name: search,
    },
    (queryEnable || selectClicked) && !props.disabled,
  );

  const banks = flatInfiniteData(banksList.data);

  const bankDetail = useBankMutation();

  const options = useSelectOptions({
    list: banksList,
    detail: bankDetail,
    selectProps: props,
    labelFormatter: (bank) => bank.name,
  });

  useEffect(() => {
    if (
      bankDetail.data?.id &&
      bankDetail.data.id === props.value &&
      !!props.onBankSelected
    ) {
      props.onBankSelected(bankDetail.data);
    }
  }, [bankDetail.data, props.value]);

  return (
    <InfiniteSelect
      fetchNextPage={banksList.fetchNextPage}
      hasNextPage={banksList.hasNextPage}
      loading={banksList.isFetching}
      options={options}
      onSelect={(bankId: number) => {
        setSelectClicked(false);

        const selectedBank = banks?.find((bank) => bank.id === bankId);

        if (!!selectedBank && !!props.onBankSelected) {
          props.onBankSelected(selectedBank);
        }
      }}
      onDropdownVisibleChange={(open) => setSelectClicked(open)}
      showSearch
      searchValue={searchValue}
      filterOption={false}
      onSearch={setSearchValue}
      {...props}
    />
  );
};

export default BanksInfiniteSelectField;
